import type {DataUploadClassOption} from 'common/helpers/dataUpload'
import {ChevronDown, ChevronRight, Search} from 'lucide-react'
import React, {FC, useId, useState} from 'react'
import {useIntl} from 'react-intl'

interface Props {
  /** Every class the pick may be made from (a subject can be offered by 150+ classes). */
  options: readonly DataUploadClassOption[]
  /** The picked class ids. */
  value: readonly number[]
  onChange: (classIds: number[]) => void
  disabled?: boolean
}

/** The browser's own 16px box, as in the Students grid: the theme's tick is oversized here. */
const CHECKBOX_STYLE: React.CSSProperties = {
  width: 16,
  height: 16,
  margin: 0,
  flexShrink: 0,
  accentColor: 'var(--bs-primary)',
  cursor: 'pointer',
}

const boardOf = (option: DataUploadClassOption) =>
  (option.curriculumShortCode || option.curriculum).trim()

/** "Grade 4-B" before "Grade 10-B". */
const byName = (a: DataUploadClassOption, b: DataUploadClassOption) =>
  (a.displayName || a.name).localeCompare(b.displayName || b.name, undefined, {numeric: true})

interface Group {
  key: string
  /** "Model Town · FBISE", only the parts that differ between groups; '' for a single group. */
  title: string
  options: DataUploadClassOption[]
}

/**
 * The options grouped by branch, then board — the parts that tell classes of one name apart.
 * A part that is the same for every option is left out of the headings; with neither varying
 * there is one untitled group.
 */
const groupOptions = (options: readonly DataUploadClassOption[]): Group[] => {
  const showBranch = new Set(options.map((option) => option.branch.trim())).size > 1
  const showBoard = new Set(options.map(boardOf)).size > 1
  const groups = new Map<string, Group>()
  options.forEach((option) => {
    const parts = [showBranch ? option.branch.trim() : '', showBoard ? boardOf(option) : '']
    const key = parts.join('\u0000')
    const group = groups.get(key) ?? {
      key,
      title: parts.filter(Boolean).join(' · '),
      options: [],
    }
    group.options.push(option)
    groups.set(key, group)
  })
  return Array.from(groups.values())
    .sort((a, b) => a.title.localeCompare(b.title, undefined, {numeric: true}))
    .map((group) => ({...group, options: [...group.options].sort(byName)}))
}

/**
 * Many classes to pick from, grouped by branch and board: a teacher's subject can be offered by
 * every class of the school (150+). Closed, it shows what is picked; open, a search box and one
 * group per branch · board, each with a checkbox that picks or clears the whole group and the
 * classes as toggles. Groups start folded (except one with a pick) when there are several; a
 * search opens every group it matches in.
 *
 * It opens in place rather than as a floating menu: it lives in a dialog, where a floating menu
 * is clipped or misplaced (see the note in SheetAssignmentModal).
 */
const ClassGroupPicker: FC<Props> = ({options, value, onChange, disabled = false}) => {
  const intl = useIntl()
  const baseId = useId()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  /** Groups the user opened or folded; the rest follow the default above. */
  const [toggled, setToggled] = useState<Record<string, boolean>>({})

  const picked = new Set(value)
  const groups = groupOptions(options)
  const needle = query.trim().toLowerCase()
  const matches = (option: DataUploadClassOption) =>
    !needle ||
    [option.displayName, option.name, option.level, option.branch, boardOf(option)].some((part) =>
      String(part ?? '')
        .toLowerCase()
        .includes(needle)
    )
  const isOpen = (group: Group) =>
    Boolean(needle) ||
    (toggled[group.key] ??
      (groups.length === 1 || group.options.some((option) => picked.has(option.classId))))

  const setPicked = (next: Set<number>) =>
    onChange(options.filter((option) => next.has(option.classId)).map((option) => option.classId))
  const toggleOption = (classId: number) => {
    const next = new Set(picked)
    if (next.has(classId)) next.delete(classId)
    else next.add(classId)
    setPicked(next)
  }
  /** The group's shown (searched) classes: all picked -> cleared, else all picked. */
  const toggleGroup = (shown: DataUploadClassOption[]) => {
    const next = new Set(picked)
    const allPicked = shown.every((option) => next.has(option.classId))
    shown.forEach((option) => (allPicked ? next.delete(option.classId) : next.add(option.classId)))
    setPicked(next)
  }

  const pickedOptions = options.filter((option) => picked.has(option.classId)).sort(byName)
  // "Grade 1-A · FBISE · Johar Town", without a part every option shares.
  const showBoard = new Set(options.map(boardOf)).size > 1
  const showBranch = new Set(options.map((option) => option.branch.trim())).size > 1
  const summary =
    pickedOptions.length === 0
      ? intl.formatMessage({id: 'DATA_UPLOAD.ALLOCATIONS.CHOOSE'})
      : pickedOptions
          .map((option) =>
            [
              option.displayName || option.name,
              showBoard ? boardOf(option) : '',
              showBranch ? option.branch : '',
            ]
              .filter(Boolean)
              .join(' · ')
          )
          .join(', ')

  return (
    <div className='class-group-picker'>
      <button
        type='button'
        className='class-group-picker-trigger'
        aria-expanded={open}
        aria-controls={`${baseId}-panel`}
        disabled={disabled || options.length === 0}
        onClick={() => setOpen((prev) => !prev)}
        title={pickedOptions.length > 0 ? summary : undefined}
      >
        <span className={`text-truncate${pickedOptions.length === 0 ? ' text-muted' : ''}`}>
          {summary}
        </span>
        {pickedOptions.length > 0 && (
          <span className='badge badge-light-primary flex-shrink-0'>{pickedOptions.length}</span>
        )}
        <ChevronDown size={16} className='flex-shrink-0 text-muted' aria-hidden />
      </button>

      {open && (
        <div className='class-group-picker-panel' id={`${baseId}-panel`}>
          <div className='d-flex align-items-center gap-2 mb-3'>
            <label className='class-group-picker-search flex-grow-1'>
              <Search size={14} aria-hidden />
              <input
                type='search'
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={intl.formatMessage({id: 'DATA_UPLOAD.ALLOCATIONS.SEARCH'})}
                aria-label={intl.formatMessage({id: 'DATA_UPLOAD.ALLOCATIONS.SEARCH'})}
              />
            </label>
            {picked.size > 0 && (
              <button
                type='button'
                className='btn btn-sm btn-link text-danger p-0'
                disabled={disabled}
                onClick={() => onChange([])}
              >
                {intl.formatMessage({id: 'DATA_UPLOAD.ALLOCATIONS.CLEAR'})}
              </button>
            )}
          </div>

          {groups.map((group) => {
            const shown = group.options.filter(matches)
            if (shown.length === 0) return null
            const count = group.options.filter((option) => picked.has(option.classId)).length
            const shownPicked = shown.filter((option) => picked.has(option.classId)).length
            const expanded = isOpen(group)
            return (
              <div key={group.key} className='class-group-picker-group'>
                {group.title && (
                  <div className='class-group-picker-group-head'>
                    <button
                      type='button'
                      className='btn btn-icon btn-sm p-0 w-auto h-auto'
                      aria-expanded={expanded}
                      aria-label={group.title}
                      onClick={() => setToggled((prev) => ({...prev, [group.key]: !expanded}))}
                    >
                      {expanded ? (
                        <ChevronDown size={16} aria-hidden />
                      ) : (
                        <ChevronRight size={16} aria-hidden />
                      )}
                    </button>
                    <label className='d-flex align-items-center gap-2 mb-0 flex-grow-1'>
                      <input
                        type='checkbox'
                        style={CHECKBOX_STYLE}
                        disabled={disabled}
                        checked={shownPicked > 0 && shownPicked === shown.length}
                        ref={(element) => {
                          if (element) {
                            element.indeterminate = shownPicked > 0 && shownPicked < shown.length
                          }
                        }}
                        onChange={() => toggleGroup(shown)}
                      />
                      <span className='fw-semibold'>{group.title}</span>
                    </label>
                    <span className='text-muted fs-8'>
                      {count}/{group.options.length}
                    </span>
                  </div>
                )}
                {expanded && (
                  <div className={`d-flex flex-wrap gap-2${group.title ? ' ms-6 mt-2' : ''}`}>
                    {shown.map((option) => {
                      const chosen = picked.has(option.classId)
                      return (
                        <button
                          key={option.classId}
                          type='button'
                          role='checkbox'
                          aria-checked={chosen}
                          disabled={disabled}
                          className={`class-question-choice${chosen ? ' is-picked' : ''}`}
                          title={[option.displayName || option.name, option.level]
                            .filter(Boolean)
                            .join(' · ')}
                          onClick={() => toggleOption(option.classId)}
                        >
                          {option.displayName || option.name}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}

          {groups.every((group) => group.options.filter(matches).length === 0) && (
            <div className='text-muted fs-7'>
              {intl.formatMessage({id: 'DATA_UPLOAD.ALLOCATIONS.NO_MATCH'})}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ClassGroupPicker
